import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Skeleton,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import PolicyIcon from "@mui/icons-material/Policy";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PersonIcon from "@mui/icons-material/Person";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { endSession } from "../session";
import { useNavigate } from "react-router-dom";
import BottomNav from "./BottomNav";
import { auth } from "../firebase";
import { getFirestore, doc, getDoc } from "firebase/firestore";

const menuItems = [
  {
    label: "Mi perfil",
    icon: <PersonIcon />,
    href: null,
    color: "#1976d2",
    dynamic: true,
  },
  {
    label: "Editar perfil",
    icon: <SettingsIcon />,
    href: "/edit",
    color: "#7c3aed",
  },
  {
    label: "Política de Privacidad",
    icon: <PrivacyTipIcon />,
    href: "/terms",
    color: "#0891b2",
  },
  {
    label: "Términos de Servicio",
    icon: <PolicyIcon />,
    href: "/terms",
    color: "#64748b",
  },
  {
    label: "Ayuda y Soporte",
    icon: <HelpOutlineIcon />,
    href: "/terms",
    color: "#f59e0b",
  },
];

const Perfil = () => {
  const [uid, setUid] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [profileImageUrl, setProfileImageUrl] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const db = getFirestore();
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnapshot = await getDoc(userDocRef);
          if (userDocSnapshot.exists()) {
            const userData = userDocSnapshot.data();
            setDisplayName(userData.displayName || "Usuario");
            setEmail(userData.email || "");
            setUid(userData.uid || user.uid);
            if (userData.profileImageUrl) setProfileImageUrl(userData.profileImageUrl);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setDisplayName("");
        setEmail("");
        setProfileImageUrl(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const navigate = useNavigate();

  const onLogout = () => {
    endSession();
    navigate("/login");
  };

  const initials = displayName
    ? displayName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f6fa", pb: 10 }}>
      {/* Hero header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1976d2 0%, #7c3aed 100%)",
          pt: 8,
          pb: 7,
          px: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
        }}
      >
        {loading ? (
          <Skeleton variant="circular" width={90} height={90} sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
        ) : (
          <Avatar
            src={profileImageUrl || undefined}
            sx={{
              width: 90,
              height: 90,
              fontSize: 32,
              fontWeight: 700,
              bgcolor: "rgba(255,255,255,0.25)",
              color: "#fff",
              border: "3px solid rgba(255,255,255,0.6)",
              boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
              mb: 1.5,
            }}
          >
            {!profileImageUrl && initials}
          </Avatar>
        )}

        {loading ? (
          <>
            <Skeleton variant="text" width={140} height={28} sx={{ bgcolor: "rgba(255,255,255,0.2)", borderRadius: 1 }} />
            <Skeleton variant="text" width={180} height={20} sx={{ bgcolor: "rgba(255,255,255,0.15)", borderRadius: 1, mt: 0.5 }} />
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, letterSpacing: 0.3 }}>
              {displayName}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mt: 0.3 }}>
              {email}
            </Typography>
          </>
        )}
      </Box>

      {/* Card flotante sobre el header */}
      <Box sx={{ px: 2, mt: 2 }}>
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 3,
            boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
            overflow: "hidden",
          }}
        >
          <List disablePadding>
            {menuItems.map((item, idx) => (
              <React.Fragment key={item.label}>
                <ListItem
                  button
                  onClick={() => navigate(item.dynamic ? `/perfil/${uid}` : item.href)}
                  sx={{
                    py: 1.6,
                    px: 2.5,
                    "&:hover": { bgcolor: "#f0f4ff" },
                    transition: "background 0.15s",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      "& svg": {
                        color: item.color,
                        fontSize: 22,
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: 15 }}
                  />
                  <ChevronRightIcon sx={{ color: "#cbd5e1", fontSize: 20 }} />
                </ListItem>
                {idx < menuItems.length - 1 && <Divider sx={{ ml: 7 }} />}
              </React.Fragment>
            ))}
          </List>
        </Box>

        {/* Cerrar sesión separado */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 3,
            boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
            mt: 2,
            overflow: "hidden",
          }}
        >
          <ListItem
            button
            onClick={onLogout}
            sx={{
              py: 1.6,
              px: 2.5,
              "&:hover": { bgcolor: "#fff5f5" },
              transition: "background 0.15s",
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, "& svg": { color: "#ef4444", fontSize: 22 } }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText
              primary="Cerrar sesión"
              primaryTypographyProps={{ fontWeight: 600, fontSize: 15, color: "#ef4444" }}
            />
          </ListItem>
        </Box>

        <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: "#94a3b8", mt: 3 }}>
          Chatio · v1.0
        </Typography>
      </Box>

      <BottomNav />
    </Box>
  );
};

export default Perfil;
